// src/components/ClaimDetail/ClaimHeader.jsx
//
// Cabecera de la ficha del reclamo, en dos filas: identidad + acciones, y la
// franja de estado ("¿qué está pasando ahora?"). Los avisos de calidad de datos
// (enlace al CRM inválido, sincronización con Organized fallida) van en un solo
// aviso compacto con la acción de marcarlos como resueltos.
import React from 'react';
import { EVENT_TYPE_LABELS } from '../../services/claimEventService';
import { claimStepLabel } from '../../services/flowService';
import { FINAL_STEPS } from '../../config/claimFlow';
import StatusBadge from '../Dashboard/StatusBadge';
import MoreMenu from '../common/MoreMenu';
import { ClaimFacts, ClaimQuickActions } from './ClaimContact';
import './ClaimDetail.css';

const DataFlags = ({ claimRecord, language, onClear }) => {
  const es = language === 'es';
  const flags = [
    claimRecord.crmLinkInvalid && String(claimRecord.crmClaimId || '').trim() === '5000'
      && { field: 'crmLinkInvalid', icon: 'fa-link-slash', text: es ? 'Enlace al CRM inválido' : 'Invalid CRM link' },
    claimRecord.oaSyncFailed
      && { field: 'oaSyncFailed', icon: 'fa-triangle-exclamation', text: es ? 'Falló la sincronización con Organized' : 'Organized sync failed' }
  ].filter(Boolean);
  if (flags.length === 0) return null;
  return (
    <ul className="claim-flags" aria-label={es ? 'Avisos de calidad de datos' : 'Data quality notices'}>
      {flags.map(flag => (
        <li key={flag.field} className="claim-flag">
          <span><i className={`fa-solid ${flag.icon}`} aria-hidden="true"></i> {flag.text}</span>
          <button type="button" className="claim-flag-resolve" onClick={() => onClear(flag.field)}>
            <i className="fa-solid fa-check" aria-hidden="true"></i> {es ? 'Marcar como resuelto' : 'Mark as resolved'}
          </button>
        </li>
      ))}
    </ul>
  );
};

export const ClaimStatusStrip = ({ claimRecord, checks, nextEvent, language }) => {
  const es = language === 'es';
  // Estado general de pago, para ver de un vistazo sin leer cada cheque.
  const paidCount = checks.filter(c => c.paid).length;
  const paymentStatus = checks.length === 0 ? 'none' : paidCount === checks.length ? 'paid' : paidCount > 0 ? 'partial' : 'pending';
  const paymentStatusLabel = {
    paid: es ? 'Pagado' : 'Paid',
    partial: es ? 'Pago parcial' : 'Partially paid',
    pending: es ? 'Pago pendiente' : 'Payment pending',
    none: es ? 'Sin cheques' : 'No checks'
  }[paymentStatus];
  // Acción pendiente más urgente: confirmar la próxima cita antes que el estado
  // de pago (una cita sin confirmar suele ser más urgente que un saldo que ya
  // tiene fecha de cobro).
  const stepLabel = claimStepLabel(claimRecord, language);
  const pendingAction = nextEvent && !nextEvent.confirmed
    ? (es ? 'Confirmar próxima cita' : 'Confirm upcoming appointment')
    : stepLabel && !FINAL_STEPS.includes(claimRecord?.flow?.stepId)
      ? `${es ? 'Paso' : 'Step'}: ${stepLabel}`
      : paymentStatus === 'pending' || paymentStatus === 'partial'
        ? (es ? 'Cobro pendiente' : 'Payment outstanding')
        : null;
  return (
    <div className="cd-status-strip">
      <div className="cd-status-strip-item">
        <span className="cd-status-strip-label"><i className="fa-regular fa-calendar" aria-hidden="true"></i> {es ? 'Próxima cita' : 'Next appointment'}</span>
        <span className="cd-status-strip-value">
          {nextEvent
            ? `${EVENT_TYPE_LABELS[nextEvent.type]?.[es ? 'es' : 'en'] || nextEvent.type} · ${nextEvent.date}${nextEvent.time ? ` ${nextEvent.time}` : ''}`
            : (es ? 'Sin citas agendadas' : 'No appointments scheduled')}
        </span>
      </div>
      <div className="cd-status-strip-item">
        <span className="cd-status-strip-label"><i className="fa-solid fa-list-check" aria-hidden="true"></i> {es ? 'Acción pendiente' : 'Pending action'}</span>
        <span className={`cd-status-strip-value ${pendingAction ? 'cd-status-strip-alert' : 'cd-status-strip-ok'}`}>
          {pendingAction || (es ? 'Al día' : 'Up to date')}
        </span>
      </div>
      {/* Solo cuenta los cheques del modelo anterior. Un reclamo que únicamente tiene
          cheques del flujo nuevo (pestaña Cheques) diría "Sin cheques" aquí, así que
          sin cheques del modelo anterior el dato se omite en vez de contradecir la ficha. */}
      {checks.length > 0 && (
        <div className="cd-status-strip-item">
          <span className="cd-status-strip-label"><i className="fa-solid fa-sack-dollar" aria-hidden="true"></i> {es ? 'Cobro CSR' : 'CSR collection'}</span>
          <span className={`cd-status-strip-value cd-status-strip-badge cd-status-strip-${paymentStatus}`}>{paymentStatusLabel}</span>
        </div>
      )}
    </div>
  );
};

// info: datos a mostrar (el cheque trae una copia vieja; la página ya mezcla
// el claimRecord vigente). claimRecord: el padre, para el estado y las banderas.
const ClaimHeader = ({ info, dataRecord, claimRecord, statusCatalog, language, canEdit, isAdmin, onEdit, onDelete, onClearFlag }) => {
  const es = language === 'es';
  const stepLabel = claimRecord ? claimStepLabel(claimRecord, language) : '';
  return (
    <header className="cd-detail-header">
      <div className="cd-detail-title-row">
        <div className="cd-detail-summary">
          <span className="cd-detail-eyebrow">
            {info.claimNumber ? `${es ? 'Reclamo' : 'Claim'} #${info.claimNumber}` : (es ? 'Reclamo · sin número aún' : 'Claim · no number yet')}
          </span>
          <h1>{info.clientName || 'N/A'} {claimRecord?.oaStatus && <StatusBadge name={claimRecord.oaStatus} catalog={statusCatalog} />}</h1>
          <p className="cd-detail-subtitle">{[info.cause, info.insuranceCompanyName].filter(Boolean).join(' · ') || 'N/A'}</p>
          {claimRecord && (
            <p className="cd-detail-step">
              <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
              {stepLabel
                ? (es ? `Paso actual: ${stepLabel}` : `Current step: ${stepLabel}`)
                : (es ? 'Sin paso asignado (reclamo anterior al flujo de pasos).' : 'No step assigned (claim predates the step flow).')}
            </p>
          )}
          {/* Accesos rápidos junto al nombre del cliente (antes estaban en la pestaña Datos). */}
          {dataRecord && <ClaimQuickActions record={dataRecord} language={language} />}
        </div>
        {(canEdit || isAdmin) && (
          <div className="cd-detail-title-actions">
            {canEdit && (
              <button type="button" className="cd-btn cd-btn-check" onClick={onEdit}>
                <i className="fa-solid fa-pen" aria-hidden="true"></i> {es ? 'Editar reclamo' : 'Edit claim'}
              </button>
            )}
            {isAdmin && (
              <MoreMenu label={es ? 'Más acciones' : 'More actions'}
                items={[{ label: es ? 'Eliminar reclamo' : 'Delete claim', icon: 'fa-trash', danger: true, onSelect: onDelete }]} />
            )}
          </div>
        )}
      </div>
      {/* Resumen de datos del reclamo: plegado, se abre con un clic. */}
      {dataRecord && <ClaimFacts record={dataRecord} language={language} defaultOpen={false} />}
      {/* Avisos de calidad de datos: solo administración, como en MyClaims.jsx. */}
      {isAdmin && claimRecord && <DataFlags claimRecord={claimRecord} language={language} onClear={onClearFlag} />}
    </header>
  );
};

export default ClaimHeader;
